import { SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { GetResetPasswordTokenResponse } from '@bandido/shared/dtos';

import { OtpInput } from '@frontend/features/common/components/OtpInput';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { client } from '@frontend/features/common/services';
import { theme } from '@frontend/theme';

import { AuthScreens } from '../types';

interface ResetPasswordCodeProps {
  onChangeScreen: (nextPage: AuthScreens) => (email?: string) => void;
  email?: string;
}

export const ResetPasswordCode = ({ onChangeScreen, email }: ResetPasswordCodeProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useSnackBar();

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const {
        data: { token },
      } = await client.post<GetResetPasswordTokenResponse>('auth/reset-password-token', { email, code });

      onChangeScreen('reset-password')(token);
    } catch (_error) {
      addToast({
        message: t('auth.resetPasswordCode.error'),
        color: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        mt: theme.spacing(6),
      }}
    >
      <Typography variant="h6" textAlign="center">
        {t('auth.resetPasswordCode.title')}
      </Typography>
      <Typography variant="body2" textAlign="center">
        {t('auth.resetPasswordCode.subtitle')} <strong>{email}</strong>
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: theme.spacing(2),
          gap: theme.spacing(3),
          width: '100%',
        }}
      >
        <OtpInput onChange={setCode} />
        <LoadingButton
          fullWidth
          size="medium"
          color="primary"
          variant="contained"
          type="submit"
          loading={isLoading}
          sx={{
            borderRadius: theme.spacing(1),
            marginTop: theme.spacing(2),
          }}
        >
          <Typography
            sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
            variant="subtitle2"
          >
            {t('auth.resetPasswordCode.continue')}
          </Typography>
        </LoadingButton>
        <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => onChangeScreen('forgot-password')()}>
          <Typography
            sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
            variant="subtitle2"
          >
            {t('auth.resetPasswordCode.back')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};
